import React from 'react';
import { Component, HBox, PuzzleCheckbox, PuzzleTextArea, VBox } from 'puzzle';
import * as _ from 'lodash'
import { PROPS, STATE, SELECTABLE_QUOTE, REQUEST_UPDATE_JSON } from './Types';
import {
  GridColumn as Column,
  GridCellProps,
  GridHeaderSelectionChangeEvent,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import QuoteComments from '../QuoteComments/QuoteComments'
import { QUOTE, QUOTE_COMMENT } from '../Types';
import { Field } from "../../../../UI/Field/Field";
import KendoGridAbstract from "../../../../util/KendoGridAbstract/KendoGridAbstract";
import classNames from "classnames";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";

class RequestQuoteUpdate extends Component<PROPS, STATE> {
  public state: STATE = {
    data: [],
    headerSelection: false,
    textAreaValue: '',
    commentsData: [],
    buttonDisabled: false
  };

  componentDidMount = () => {
    this.getQuotesData(true);
  };
  /**
   * method for loading data
   * @param checkSwitch if true method will only set state , if false method will be used for getting query params
   */
  getQuotesData = (checkSwitch: boolean) => {
    const { quotes } = this.props.quotesData;
    const { vendorId } = this.props.vendor;
    let data = quotes.reduce((array: SELECTABLE_QUOTE[], quote: QUOTE) => this.setGridData(array, quote, vendorId), []);
    if (checkSwitch) {
      this.setState({ data });
    } else {
      return this.getQuoteIds(data);
    }
  };

  /**
   * Method for getting available quote ids
   */
  getQuoteIds = (data: SELECTABLE_QUOTE[]): string => data.map((quote: SELECTABLE_QUOTE) => quote.quoteId).join(',');


  /**
   * Precision for rounding BIG JS objects
   */
  getPrecision = () => this.props.quotesData.quoteConfiguration.numberPrecision;

  /**
   * method for setting grid data
   */
  setGridData = (array: SELECTABLE_QUOTE[], quote: QUOTE, vendorId: number): SELECTABLE_QUOTE[] => {
    if (vendorId === quote.vendorId && _.includes(['COUNTERED', 'PROVIDED', 'UPDATED'], quote.status)) {
      let selectableQuote: SELECTABLE_QUOTE;
      selectableQuote = { ..._.cloneDeep(quote), selected: quote.status === 'COUNTERED' };
      array.push(selectableQuote);
    }
    return array;
  };

  /**
   * handler for clicking on checkbox cell
   * * @param dataItem
   */
  selectionChange = (dataItem: SELECTABLE_QUOTE, selectionHandler: Function | undefined, syntheticEvent: CheckboxChangeEvent): void => {
    const { data } = this.state;
    let stateCopy: SELECTABLE_QUOTE[];
    stateCopy = _.cloneDeep(data);
    let index = _.findIndex(data, (quote) => {
      return quote === dataItem;
    })
    if (index >= 0) {
      stateCopy[index].selected = !dataItem.selected;
      this.setState({ data: stateCopy }, this.handleButtonChange);
    }
    if (selectionHandler) {
      selectionHandler({ syntheticEvent });
    }
  };

  /**
   * handler for clicking on select all/deselect all checkbox in header
   * * @param event
   */
  headerSelectionChange = (event: CheckboxChangeEvent | GridHeaderSelectionChangeEvent) => {
    const checked = event.nativeEvent.target.checked;
    let stateCopy: SELECTABLE_QUOTE[];
    stateCopy = _.cloneDeep(this.state.data);
    stateCopy.forEach(quote => {
      if (quote.status !== 'COUNTERED') {
        quote.selected = checked;
      }
    })
    this.setState({ data: stateCopy, headerSelection: checked }, this.handleButtonChange);
  };

  /**
   * renderer for checkbox column
   * * @param cellProps
   */
  selectCellRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem, selectionChange } = cellProps;
    const { selected, status } = dataItem;
    return (
      <td className={'tp-select-file'}>
        <PuzzleCheckbox
          checked={selected}
          disabled={status === 'COUNTERED'}
          onChange={this.selectionChange.bind(this, dataItem, selectionChange)}
        />
      </td>
    )
  };

  /**
   * renderer for language column
   * * @param cellProps
   */
  cellRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { field } = cellProps;
    const value = field && cellProps.dataItem.quoteScope[field];

    return (
      <td>
        <span className={'sr-only'}>
          {field && this.translate(field)}
        </span>
        {value}
      </td>
    )
  };

  /**
   * amount cell renderer
   * * @param cellProps
   */
  amountCellRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem } = cellProps;
    const { totalWithoutOptionals, currency, status } = dataItem;
    const precision = this.getPrecision();
    let roundedTotal = totalWithoutOptionals.toFixed(precision);
    let currencyString = this.translate('currencyTotalFormat', { currency });
    let roundedTotalValue = this.translate('currencyTotalFormat', { roundedTotal });
    const quoteStatus = status === 'COUNTERED' ?
      (<i aria-hidden={true} className={'tp-quote-status requested tp-icon tp-icon-sm tp-undo-flat'} />) :
      (status === 'UPDATED') ? (
        <i aria-hidden={true} className={'tp-quote-status updated tp-icon tp-icon-sm tp-update'} />) : '';
    const amountCls = classNames(
      'tp-cell-amount',
      {
        'requested': status === 'COUNTERED',
        'updated': status === 'UPDATED',
      })
    return (
      <td className={'tp-request-quote-amount'}>
        <HBox className={amountCls}>
          <span className={'sr-only'}>
          {this.translate('amount')}
        </span>
          <span className={'pd-quote-currency'}>{currencyString}</span>
          <span className={'pd-quote-value'}>{roundedTotalValue}</span>
          {quoteStatus}
        </HBox>
      </td>
    );
  };

  /**
   * controlling text area value and handling on change event
   * * @param event
   */
  handleChangeTextArea = (event: any) => {
    const { value } = event;
    this.setState({ textAreaValue: value }, this.handleButtonChange)
  };

  /**
   * method for handling button change disable | enable
   */
  handleButtonChange = (): void => {
    const { textAreaValue, data } = this.state;
    let disabled = textAreaValue.length >= 10;
    let quoteSelected = data.some((quote: SELECTABLE_QUOTE) => {
      return quote.selected && quote.status !== 'COUNTERED'
    })
    this.dispatch('updateRequestUpdateButton', '', { buttonDisabled: disabled && quoteSelected })
  }

  /**
   * POST method
   */
  handleSubmitUpdateRequest = (): REQUEST_UPDATE_JSON[] => {
    const { data } = this.state;
    const jsonForPost = data.reduce((array: REQUEST_UPDATE_JSON[], quote: SELECTABLE_QUOTE) => this.getSelectedQuotes(array, quote), []);
    return jsonForPost;
  };

  /**
   * Get  method query params
   */
  getQueryParams = () => ({ quoteIds: this.getQuotesData(false) })

  /**
   * get json for POST request
   */
  getSelectedQuotes = (array: REQUEST_UPDATE_JSON[], quote: SELECTABLE_QUOTE): REQUEST_UPDATE_JSON[] => {
    if (quote.status !== 'COUNTERED' && quote.selected) {
      let requestQuote: REQUEST_UPDATE_JSON;
      const { textAreaValue } = this.state;
      requestQuote = { comment: textAreaValue, quoteId: quote.quoteId };
      array.push(requestQuote);
    }
    return array;
  };

  /**
   * header checkbox update
   */
  onSelectionChange = (event: GridSelectionChangeEvent): void => {
    const { data } = this.state;
    data.forEach((item: SELECTABLE_QUOTE) => {
      if (item.quoteId === event.dataItem.quoteId) {
        item.selected = !event.dataItem.selected;
      }
      return item;
    })
    const headerSelection = data.every(item => item.selected);
    this.setState({ data, headerSelection });
  }

  /**
   * set checkbox value
   */
  getCheckboxValue = (): boolean => {
    const { data } = this.state;
    const checkboxDisableFlag = data.findIndex((dataItem: SELECTABLE_QUOTE) => !dataItem.selected);
    return checkboxDisableFlag === -1;
  }

  /**
   * Custom selected column header cell render
   */
  selectedHeaderColumnCellRender = () => {
    const { headerSelection } = this.state;
    return (
      <PuzzleCheckbox
        checked={headerSelection}
        className={'tp-drawer-files-grid-header-select-cell'}
        onChange={this.headerSelectionChange}
        aria-label={this.translate("SRL-checkboxHeaderLabel")}
      />
    )
  };

  /**
   * setter for comments data
   */
  setCommentsData = (commentsData: QUOTE_COMMENT[]): void => {
    this.setState({ commentsData })
  }

  renderBody(): JSX.Element {
    const { data, commentsData } = this.state;
    const { quoteScope, phaseQuotes } = this.props.quotesData.quoteConfiguration.quoteLevel

    let wrapperProps = {
      ...(commentsData?.length > 0 ? { className: 'pd-request-quote-grid-wrapper comments-bellow' } : { className: 'pd-request-quote-grid-wrapper' })
    };

    return (
      <>
        <KendoGridAbstract
          rowHeight={48}
          data={data}
          captionText={this.translate('SRL-requestQuoteTableCaptionText')}
          selectedField='selected'
          onSelectionChange={this.onSelectionChange}
          onHeaderSelectionChange={this.headerSelectionChange}
          wrapperProps={wrapperProps}
        >
          <Column
            field='selected'
            cell={this.selectCellRenderer}
            headerCell={this.selectedHeaderColumnCellRender}
            headerClassName={'tp-select-file'}
            headerSelectionValue={this.getCheckboxValue()}
            width='50px'
          />
          <Column
            field={'languageName'}
            cell={this.cellRenderer}
            title={this.translate('languageName')}
          />
          {quoteScope === 'JOB' ?
            <Column
              field={'batchName'}
              cell={this.cellRenderer}
              title={this.translate('batchName')}
            /> : null}
          {phaseQuotes ?
            <Column
              field='phaseName'
              cell={this.cellRenderer}
              title={this.translate('phaseName')}
            /> : null}
          <Column
            field={'approvedQuoteGrandTotal'}
            title={this.translate('amount')}
            cell={this.amountCellRenderer}
            width='150px'
          />
        </KendoGridAbstract>
        <VBox className={'tp-dialog-bottom-section'} gap={4}>
          {!!commentsData?.length &&
            <QuoteComments comments={commentsData} userName={this.props.userName} />}
          <Field mandatory>
            <PuzzleTextArea
              label={this.translate('textAreaLabel', { className: 'tp-text-regular' })}
              onChange={this.handleChangeTextArea}
            />
          </Field>
        </VBox>
      </>
    );
  }
}

export default RequestQuoteUpdate;
